import * as React from "react";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import Dashboard from "@uppy/dashboard";
import Spanish from "@uppy/locales/lib/es_ES";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import '../App.css';

const { useEffect, useState } = React;

interface UploadSuccessResponse {
  videourl: string;
}

const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now(); // Current Unix timestamp
  const extension = originalName.includes('.') 
    ? originalName.split('.').pop() 
    : '';
  const baseName = originalName.includes('.') 
    ? originalName.substring(0, originalName.lastIndexOf('.')) 
    : originalName;
  
  // Clean the base name to remove special characters and spaces
  const cleanBaseName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_');
    
  return `${cleanBaseName}_${timestamp}${extension ? `.${extension}` : ''}`;
};

const useUppyWithSupabase = () => {
  const [uppy] = useState(() => 
    new Uppy({
      restrictions: {
        maxFileSize: 20 * 1024 * 1024 * 1024, // 20GB in bytes
        maxNumberOfFiles: 10,
        allowedFileTypes: [
          'video/*',
          'image/*'
        ]
      },
      autoProceed: true,
      locale: Spanish
    })
  );

  useEffect(() => {
    const initializeUppy = async () => {
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('token');
      const contratacionId = params.get('contratacionId');
      const returnUrl = params.get('returnUrl');
      const bucketName = params.get('bucketName') || 'contrataciones_creaciones';

      if (!authToken) {
        console.error('No authentication token provided');
        return;
      }

      if (!contratacionId) {
        console.error('No contratacion ID provided');
        return;
      }

      uppy.use(Tus, {
        endpoint: `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${authToken}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024,
        allowedMetaFields: [
          "bucketName",
          "objectName",
          "contentType",
          "cacheControl",
        ],
        onError: (error: Error) => {
          console.error("Upload error:", error);
          // You could add more specific error handling here if needed
        },
      });

      // Track successful uploads
      let successfulUploads: string[] = [];

      uppy.on('file-added', (file: any) => {
        // Generate a unique filename for each file
        const uniqueFileName = generateUniqueFileName(file.name);
        
        // Store the original name if needed
        file.meta.originalName = file.name;
        
        // Update the file name
        file.name = uniqueFileName;
        
        file.meta = {
          ...file.meta,
          bucketName,
          objectName: uniqueFileName,
          contentType: file.type,
        };
      });

      // Handle successful uploads
      uppy.on('upload-success', async (file: any, response: any) => {
        console.log('Upload success - File:', file);
        console.log('Upload success - Response:', response);

        // Construct the public URL for the uploaded file
        const publicUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${bucketName}/${file.name}`;
        successfulUploads.push(publicUrl);

        // Check if all files have been uploaded
        const uploadedCount = successfulUploads.length;
        const totalFiles = uppy.getFiles().length;

        if (uploadedCount === totalFiles) {
          try {
            // Call the Supabase function with all URLs
            const functionResponse = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/rpc/contratacion_subida_creacion`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                  'Apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
                },
                body: JSON.stringify({
                  p_video_url: successfulUploads,
                  p_contratacion_id: contratacionId
                }),
              }
            );

            if (!functionResponse.ok) {
              throw new Error('Failed to update contratacion');
            }

            // Handle redirect after successful function call
            if (returnUrl) {
              const baseReturnUrl = decodeURIComponent(returnUrl);
              const separator = baseReturnUrl.includes('?') ? '&' : '?';
              const urlsParam = successfulUploads.map(url => encodeURIComponent(url)).join(',');
              const finalReturnUrl = `${baseReturnUrl}${separator}videourls=${urlsParam}`;
              
              console.log('Redirecting to:', finalReturnUrl);
              window.location.href = finalReturnUrl;
            }
          } catch (error) {
            console.error('Error calling Supabase function:', error);
          }
        }
      });
    };

    initializeUppy();

    return () => (uppy as any).close();
  }, [uppy]);

  return uppy;
};

const FileUploaderContrato = () => {
  const [error, setError] = useState('');
  const uppy = useUppyWithSupabase();

  useEffect(() => {
    uppy.use(Dashboard, {
      inline: true,
      target: "#drag-drop-area",
      height: 450,
      width: '100%',
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: 'Sube hasta 10 archivos (imágenes o videos). Máximo 20GB por archivo.',
      showRemoveButtonAfterComplete: false,
      hideCancelButton: true,
      doneButtonHandler: null,
    });
  }, [uppy]);

  return (
    <div className="file-uploader-container">
      <div className="file-uploader-box">
        <div className="header-mini">
            <h1 className="upload-title">Sube tu contenido</h1>
            <img src="/Logo_Monomo-02.png" alt="Logo" className="logo" />
        </div>
        <div id="drag-drop-area" />
        {error && (
          <p className="error-message">{error}</p>
        )}
      </div>
    </div>
  );
};

export default FileUploaderContrato;